/**
 * GrowthChip — inline momentum chip for creator/clan headers.
 *
 * Renders one of:
 *   nothing               — no data yet (silent)
 *   📈 +12.5% this week   — positive momentum
 *   📉 -3.2% this week    — negative
 *   🚀 Momentum           — momentum_score >= 70 (breakout badge)
 *
 * Pulls `/api/creators/{handle}/growth` — public endpoint, no auth needed.
 */
import React, { useEffect, useState } from 'react';
import api from '../api';

export default function GrowthChip({ handle, size = 'sm', showAlways = false }) {
  const [growth, setGrowth] = useState(null);

  useEffect(() => {
    if (!handle) return;
    let cancelled = false;
    api.get(`/creators/${handle}/growth`)
      .then((r) => { if (!cancelled) setGrowth(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [handle]);

  if (!growth) return null;

  const best = growth.best_platform ? growth.by_platform[growth.best_platform] : null;
  if (!best || best.followers === 0) return null;

  const pct = best.pct_7d;
  const score = growth.overall_score;
  const isPositive = pct > 0.5;
  const isNegative = pct < -0.5;
  const isBreakout = score >= 70;

  // Skip flat momentum unless caller wants us to always show something
  if (!isPositive && !isNegative && !showAlways) return null;

  const bgColor = isBreakout ? 'rgba(255,87,34,0.15)'
    : isPositive ? 'rgba(74,222,128,0.12)'
    : isNegative ? 'rgba(239,68,68,0.12)'
    : 'rgba(255,255,255,0.05)';
  const borderColor = isBreakout ? 'rgba(255,87,34,0.4)'
    : isPositive ? 'rgba(74,222,128,0.35)'
    : isNegative ? 'rgba(239,68,68,0.35)'
    : 'rgba(255,255,255,0.1)';
  const textColor = isBreakout ? '#FF5722'
    : isPositive ? '#4ade80'
    : isNegative ? '#EF4444'
    : 'inherit';
  const label = isBreakout ? `🚀 Momentum · +${pct}% this week`
    : isPositive ? `📈 +${pct}% this week`
    : isNegative ? `📉 ${pct}% this week`
    : `→ Flat this week`;

  const fontSize = size === 'lg' ? 13 : size === 'md' ? 12 : 11;
  const padding = size === 'lg' ? '5px 10px' : '3px 8px';

  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding, borderRadius: 999,
        background: bgColor, border: `1px solid ${borderColor}`,
        color: textColor, fontSize, fontWeight: 700, whiteSpace: 'nowrap',
      }}
      title={`Momentum score ${score}/100 · ${best.followers.toLocaleString()} followers on ${best.platform.toUpperCase()}`}
      data-testid={`growth-chip-${handle}`}
    >
      {label}
    </span>
  );
}
